import {
  asGradingId,
  getGradingDisplayNames,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  SlideoverDialog,
  SlideoverDialogContent,
  SlideoverDialogHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { FullAbsoluteTimestamp } from "@repo/ui/timestamps/fullAbsoluteTimestamp";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconSparkles } from "@tabler/icons-react";
import { useRef, type FC } from "react";
import { Heading } from "react-aria-components";
import { Outlet, useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { usePlaybookGradingQuery } from "../../api/entities/performancePlaybook.js";
import { AudioPlayerProvider } from "../../components/audioPlayer/provider.js";
import {
  TabsLayoutTab,
  TabsLayoutTabs,
} from "../../components/layout/tabsLayout.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { CancelCallTaskButton } from "./cancelCallTaskButton.js";
import type { TranscriptScrollPosition } from "./trainingTeammatePageOverviewGradingContext.js";

export const TrainingTeammatePageOverviewGrading: FC = () => {
  const { gradingId: gradingIdParam } = useParams();
  if (!gradingIdParam) {
    throw new Error("Missing params.");
  }
  const gradingId = asGradingId(gradingIdParam);

  const { data: grading, isLoading: isGradingLoading } =
    usePlaybookGradingQuery({ gradingId });
  const transcriptScrollPositionRef = useRef<TranscriptScrollPosition | null>(
    null,
  );

  const renderDialogLabel = () => {
    if (!grading) {
      return "Loading call...";
    }

    const { primaryDisplayName } = getGradingDisplayNames(grading);
    return `Call with ${primaryDisplayName}`;
  };

  const renderHeaderLeft = () => {
    // Show a skeleton when the contact has not loaded yet.
    if (!grading) {
      return null;
    }

    const {
      primaryDisplayName,
      secondaryDisplayName,
      isGeneratedFromTranscript,
    } = getGradingDisplayNames(grading);

    return (
      <div className="flex min-w-0 items-center gap-2">
        <Heading
          slot="title"
          className={twMerge(
            "truncate text-base font-semibold text-foreground",
            PH_MASK_CLASS,
          )}
        >
          {primaryDisplayName}
        </Heading>
        {isGeneratedFromTranscript ? (
          <TooltipTrigger>
            <TooltipHoverTarget shape="rounded">
              <IconSparkles className="size-4 shrink-0 text-purple-600" />
            </TooltipHoverTarget>
            <SimpleTooltip>
              This name was found in the transcript. Make sure to check for
              accuracy.
            </SimpleTooltip>
          </TooltipTrigger>
        ) : null}
        {secondaryDisplayName && (
          <span
            className={twMerge(
              "flex-1 truncate text-xs text-gray-600",
              PH_MASK_CLASS,
            )}
          >
            {secondaryDisplayName}
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <SlideoverDialog label={renderDialogLabel()}>
        <AudioPlayerProvider>
          <SlideoverDialogHeader
            isLoading={isGradingLoading}
            className="h-12 border-b-0 bg-card"
            right={
              <>
                {grading?.last_recorded_at ? (
                  <FullAbsoluteTimestamp
                    timestamp={grading.last_recorded_at}
                    className="relative -top-px hidden sm:inline"
                  />
                ) : null}
                {grading?.call_activity?.cancelable_call_task ? (
                  <CancelCallTaskButton
                    callTask={grading.call_activity.cancelable_call_task}
                    gradingId={grading.id}
                    teammateAgentId={grading.teammate_id}
                    customerName={
                      getGradingDisplayNames(grading).primaryDisplayName
                    }
                  />
                ) : null}
              </>
            }
          >
            {renderHeaderLeft()}
          </SlideoverDialogHeader>
          {grading?.last_recorded_at ? (
            <div className="bg-card px-4 pb-1 sm:hidden">
              <FullAbsoluteTimestamp
                timestamp={grading.last_recorded_at}
                className="block whitespace-normal"
              />
            </div>
          ) : null}
          <div className="border-b border-border-light bg-card px-4">
            <TabsLayoutTabs isLoading={isGradingLoading} className="min-h-9">
              <TabsLayoutTab to="transcript">Transcript</TabsLayoutTab>
              <TabsLayoutTab to="assessment">Performance</TabsLayoutTab>
            </TabsLayoutTabs>
          </div>
          <SlideoverDialogContent>
            <Outlet context={{ transcriptScrollPositionRef }} />
          </SlideoverDialogContent>
        </AudioPlayerProvider>
      </SlideoverDialog>
    </>
  );
};
